import React, { useState, useEffect } from 'react';
import { Modal, ModalActions, ModalBody } from 'components/ui/modal';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import { useGroups } from 'hooks/unifiedview/useReports';
import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import LoadingCircle from 'components/ui/LoadingCircle';
import Alert from 'components/ui/Alert';
import classNames from 'classnames';
import TextArea from 'components/v2/forms/TextArea';
import ToggleSwitchGroup from 'components/v2/ToggleSwitchGroup';
import { partition } from 'lodash';
import { useTranslation } from 'react-i18next';

const defaultReport = {
  name: '',
  description: '',
  group: null,
  region: null,
  shared: true,
};

export default function SaveModal({ report, isOpen, onClose, onSave, isNew }) {
  const { data: groupsData, isSuccess, isLoading } = useGroups();
  const [errors, setErrors] = useState({});
  const [formFields, setFormFields] = useState(defaultReport);
  const [groups, regions] = partition(groupsData, ['parent', null]);
  const { t } = useTranslation();

  useEffect(() => {
    let group = {};

    const region = report
      ? regions.find((data) => data._id === report.groupId) || {}
      : {};

    if (region?.parent) {
      group = groups.find((data) => data._id === region.parent);
    } else if (report?.groupId) {
      group = groups.find((data) => data._id === report.groupId);
    }

    setFormFields({
      group,
      region,
      ...(report || defaultReport),
    });
  }, [report]);

  const activeRegions =
    regions.filter((region) => region.parent === formFields?.group?._id) || [];

  function handleSave() {
    const errors = validate();

    setErrors(errors);
    if (Object.keys(errors).length === 0 && onSave) {
      const { name, group, region, description, shared } = formFields;
      const data = {
        name,
        groupId: region?._id || group?._id,
        description,
        shared,
        ...(report && !isNew && { id: report._id }),
      };
      onSave(data);
    }
  }

  const actionLabel = isNew ? t('SAVE_NEW') : t('SAVE');
  const title = isNew ? t('SAVE_NEW_REPORT') : t('UPDATE_REPORT');

  function handleChange(field) {
    return function (value) {
      setFormFields((prev) => ({
        ...prev,
        [field]: value,
      }));
    };
  }
  const primaryAction = {
    label: actionLabel,
    onClick: handleSave,
  };

  const secondaryActions = [
    {
      label: t('CANCEL'),
      onClick: onClose,
    },
  ];

  function handleSelectGroup(group) {
    setFormFields((prev) => ({
      ...prev,
      group,
      region: null,
    }));
  }

  function handleSelectRegion(region) {
    setFormFields((prev) => ({
      ...prev,
      region,
    }));
  }

  function renderErrors() {
    const errorList = Object.values(errors);
    if (errorList.length === 0) {
      return;
    }

    return (
      (<Alert
        dataTestId="Alert-2ef35e64"
        type="error"
        title={t('PLEASE_ADDRESS_THE_FOLLOWING_ISSUES')}
        className="mb-4">
        <ul data-testid="ul-19a8cec1">
          {errorList.map((message, i) => (
            <li data-testid={`li-e4aad8af-${i}`} key={i}>{message}</li>
          ))}
        </ul>
      </Alert>)
    );
  }

  function validate() {
    const errors = {};

    const maxNameLength = 100;

    const { name, group } = formFields;

    if (!name?.trim()) {
      errors.name = t('PLEASE_PROVIDE_THE_REPORT_NAME');
    }

    if (name?.length > maxNameLength) {
      errors.name = t('TOO_BIG_AMOUNT_ERROR', { maxAmount: maxNameLength });
    }

    if (!group) {
      errors.group = t('PLEASE_SELECT_A_GROUP');
    }
    return errors;
  }

  function renderForm() {
    return (<>
      <ModalBody dataTestId="ModalBody-700b0e76">
        {renderErrors()}
        <div
          data-testid="div-95a86128"
          className={classNames('mb-4', { error: !!errors.name })}>
          <Label dataTestId="Label-487d4bca" className="mb-1">{t('REPORT_NAME')}*</Label>
          <TextField
            dataTestId="TextField-1abd63c5"
            value={formFields?.name}
            onChange={handleChange('name')}
            error={!!errors.name} />
        </div>
        <div
          data-testid="div-39a40491"
          className={classNames('mb-4', { error: !!errors.description })}>
          <Label dataTestId="Label-86ab1ea4" className="mb-1">{t('DESCRIPTION')}</Label>
          <TextArea
            dataTestId="TextArea-36e5deb1"
            value={formFields?.description}
            onChange={handleChange('description')}
            error={!!errors.description} />
        </div>
        <div data-testid="div-e791b93d" className="grid grid-cols-2 gap-4 mb-4">
          <div
            data-testid="div-ada61555"
            className={classNames('mt-4', { error: !!errors.group })}>
            <Label dataTestId="Label-ce0028c9" className="mb-1">{t('SELECT_GROUP')}*</Label>
            <CustomDropdown
              dataTestId="CustomDropdown-7cf1efb5"
              options={groups || []}
              onChange={handleSelectGroup}
              error={!!errors.group}
              value={formFields?.group} />
          </div>
          {activeRegions?.length > 0 && (
            <div data-testid="div-5f63f6ea" className="my-4">
              <Label dataTestId="Label-2564b714" className="mb-1">{t('SELECT_REGION')}</Label>
              <CustomDropdown
                dataTestId="CustomDropdown-6e6c6e4c"
                options={
                  activeRegions.filter(
                    (region) => region.parent === formFields?.group?._id,
                  ) || []
                }
                onChange={handleSelectRegion}
                error={!!errors.region}
                value={formFields?.region} />
            </div>
          )}
        </div>
        <div data-testid="div-91e977eb" className="my-4 flex items-center">
          <ToggleSwitchGroup
            dataTestId="ToggleSwitchGroup-c00ba1b7"
            onChange={handleChange('shared')}
            enabled={formFields?.shared}>
            {t('PUBLIC')}?
          </ToggleSwitchGroup>
        </div>
      </ModalBody>
      <ModalActions
        dataTestId="ModalActions-693d3de2"
        primaryAction={primaryAction}
        secondaryActions={secondaryActions} />
    </>);
  }
  return (
    (<Modal
      dataTestId="Modal-d6ad23aa"
      title={title}
      isOpen={isOpen}
      onClose={onClose}>
      {isLoading && (
        <ModalBody dataTestId="ModalBody-b5630a66">
          <LoadingCircle dataTestId="LoadingCircle-ddcbc06f" />
        </ModalBody>
      )}
      {isSuccess && renderForm()}
    </Modal>)
  );
}
